import { format } from 'date-fns';
import { type ComponentProps, memo, useCallback, useMemo } from 'react';

import { Clock } from '@air/next-icons';
import { Avatar } from '@air/primitive-avatar';
import { HoverCard } from '@air/primitive-hover-card';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { formatDuration, formatFullName, parseAirDateToISO } from '@air/utilities';
import { cn } from '@air/utils-tailwind';

import { usePlayerStore } from './PlayerContextProvider';
import { type Marker } from './PlayerSeekbarTimestamps';

const PLAYER_SEEKBAR_TIMESTAMP_MARKER_HOVER_CARD_DELAY_IN_MS = 300;

export type PlayerSeekbarTimestampsMarkerProps = ComponentProps<'div'> & {
  highlight?: boolean;
  marker: Marker;
  onMarkerSelect?: (marker: Marker) => void;
};

export const PlayerSeekbarTimestampsMarker = memo(
  ({ className, highlight = false, marker, onMarkerSelect, ...restOfProps }: PlayerSeekbarTimestampsMarkerProps) => {
    const duration = usePlayerStore((state) => state.duration);

    const handleClick = useCallback(() => {
      onMarkerSelect?.(marker);
    }, [marker, onMarkerSelect]);

    const topLevelComment = useMemo(() => {
      return marker.discussion.comments.data.at(0);
    }, [marker.discussion]);

    return (
      <div
        className={cn('absolute inset-y-0', className)}
        data-testid="PLAYER_SEEKBAR_TIMESTAMP_MARKER"
        style={{
          left: duration > 0 ? `${(marker.timestamp / duration) * 100}%` : 0,
        }}
        {...restOfProps}
      >
        <HoverCard
          className="p-4"
          openDelay={PLAYER_SEEKBAR_TIMESTAMP_MARKER_HOVER_CARD_DELAY_IN_MS}
          side="top"
          trigger={
            <button
              className="focus-visible:focus pointer-events-auto absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full outline-none ring-2 ring-transparent transition-[box-shadow] hover:ring-white/80 data-[highlight=true]:ring-white/80"
              data-highlight={highlight ? 'true' : 'false'}
              data-testid="PLAYER_SEEKBAR_TIMESTAMP_MARKER_BUTTON"
              onClick={handleClick}
              type="button"
            >
              <VisuallyHidden>Jump to {formatDuration(marker.timestamp)}</VisuallyHidden>
              <Avatar
                className="cursor-pointer"
                colorSeed={marker.userId}
                data-commenter-id={marker.userId}
                data-testid="PLAYER_SEEKBAR_TIMESTAMP_MARKER_AVATAR"
                size={20}
                src={marker.userAvatar}
                text={marker.initials}
              />
            </button>
          }
        >
          <div className="flex w-72 flex-col gap-1">
            <header className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Avatar colorSeed={marker.userId} size={20} src={marker.userAvatar} text={marker.initials} />
                <div className="truncate font-medium text-12 text-grey-12">
                  {formatFullName(topLevelComment?.account.firstName, topLevelComment?.account.lastName)}
                </div>
                <div className="flex shrink-0 items-center gap-0.5 text-10 text-grey-10">
                  <Clock className="size-3" />
                  <time className="tabular-nums" dateTime={marker.timestamp.toString()}>
                    {formatDuration(marker.timestamp)}
                  </time>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {topLevelComment?.createdAt && (
                  <time className="text-12 text-grey-10" dateTime={topLevelComment.createdAt}>
                    {format(parseAirDateToISO(topLevelComment.createdAt), 'MMM dd')}
                  </time>
                )}
                <div className="px-1 font-bold text-10 text-grey-9 tabular-nums">V{marker.discussion.assetVersion}</div>
              </div>
            </header>
            <p className="whitespace-pre-line pl-7 text-14 text-grey-12">{topLevelComment?.body}</p>
          </div>
        </HoverCard>
      </div>
    );
  },
);

PlayerSeekbarTimestampsMarker.displayName = 'PlayerSeekbarTimestampsMarker';
